import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import classNames from "classnames";
import { addMonths } from "date-fns";
import { useEffect, useState } from "react";
import { TrainingService } from "../../api/TrainingService";
import useTranslation from "../../hooks/useTranslation";
import { Activity } from "../../models/activity";
import SurveyDispatchManager from "../../models/surveyDispatchManager";
import TrainingModule from "../../models/trainingModule";
import { ActivityType } from "../../types/actionPlan";
import { TrainingModuleResponse } from "../../types/training";
import {
  generateSwimlanesData,
  getRailLabels,
  getTimelineRange,
} from "../../utils/ActionPlan.utils";
import { sortTrainingModules } from "../../utils/Training.utils";
import { ActionPlanActivity } from "../ActionPlanActivity/ActionPlanActivity";
import { getTrainingModuleStates } from "../ActionPlanSetupSteps/actionPlanSetup.utils";
import { ActionPlanTrainingWizard } from "../ActionPlanTrainingWizard/ActionPlanTrainingWizard";
import { NoDataState } from "../NoDataState/NoDataState";
import { Button } from "../ui/Button/Button";
import { Modal } from "../ui/Modal/Modal";
import { Direction } from "../ui/icons";
import { ActionPlanNowIndicator } from "./ActionPlanNowIndicator";
import { ActionPlanRail } from "./ActionPlanRail";
import { ActionPlanSwimlane } from "./ActionPlanSwimlane";
import "./ActionPlanTimeline.scss";

interface ActionPlanTimelineProps {
  dispatchManager?: SurveyDispatchManager;
  activities: Activity[];
  now: Date; // For testing purposes
  rangeLength?: number;
  isSettingUp?: boolean;
  hasSurveyAccess?: boolean;
  onActivitiesAdd: (activities: Activity[]) => void | Promise<void>;
  onActivityUpdate: (activity: Activity) => void | Promise<void>;
  onActivityRemove: (activity: Activity) => void | Promise<void>;
  onActivitiesRefetch?: () => void;
}

export const ActionPlanTimeline = ({
  dispatchManager,
  activities,
  now,
  rangeLength = 6,
  isSettingUp = false,
  hasSurveyAccess,
  onActivitiesAdd,
  onActivityUpdate,
  onActivityRemove,
  onActivitiesRefetch,
}: ActionPlanTimelineProps) => {
  const { t, i18n } = useTranslation("actionPlanPage");
  const [selectedActivity, setSelectedActivity] = useState<Activity>();
  const [activityDetailModalOpen, setActivityDetailModalOpen] = useState(false);
  const [timelineOffset, setTimelineOffset] = useState(0);
  const [addActivityModalOpen, setAddActivityModalOpen] = useState(false);
  const [trainingModules, setTrainingModules] = useState<TrainingModule[]>([]);
  const trainingService = new TrainingService();

  const baseRange = getTimelineRange(activities || [], now, rangeLength);
  const startDate = addMonths(baseRange.startDate, timelineOffset);
  const endDate = addMonths(baseRange.endDate, timelineOffset);
  const labels = getRailLabels(startDate, endDate, i18n.language);

  const lanes = generateSwimlanesData(activities || [], t);

  const { data: trainingModulesData, error: trainingModulesError } = useQuery<
    TrainingModuleResponse[],
    AxiosError
  >(["trainingModules"], () => trainingService.getTrainingModules());
  const { activeModules } = sortTrainingModules(trainingModules);

  useEffect(() => {
    if (trainingModulesData) {
      const trainingModulesFromResponse = trainingModulesData.map(
        (module) => new TrainingModule(module)
      );

      setTrainingModules(trainingModulesFromResponse);
    }
  }, [trainingModulesData]);

  const onSurveyUpdate = () => {
    if (dispatchManager && onActivitiesRefetch) {
      onActivitiesRefetch();
      setActivityDetailModalOpen(false);
    }
  };

  const handleNavigate = (direction: Direction) =>
    setTimelineOffset((v) => v + (direction === Direction.RIGHT ? 1 : -1));

  const handleActivityClick = (activity: Activity) => {
    setSelectedActivity(activity);
    setActivityDetailModalOpen(true);
  };

  const [dateChangeError, setDateChangeError] = useState<string>();

  const handleChangeDate = (
    activity: Activity,
    eventIndex: number,
    newDate: Date
  ) => {
    const updatedActivity = activity.updateEventDate(eventIndex, newDate);

    if (!updatedActivity.validateEventsOrder()) {
      setDateChangeError(t("timeline.errors.touchpointOrderError") as string);
    } else {
      setDateChangeError(undefined);
    }

    setSelectedActivity(updatedActivity);
  };

  const trainingModuleStates = getTrainingModuleStates(
    activities || [],
    activeModules
  );

  const handleActivitiesAdd = async (suggestedActivities: Activity[]) => {
    await onActivitiesAdd(suggestedActivities);
  };

  const getSurveyDispatch = (activity: Activity | undefined) => {
    const id = activity?.extraProperties?.dispatch?.id;
    return id ? dispatchManager?.getDispatchById(id) : undefined;
  };

  const renderError = () => (
    <NoDataState
      title={t("addModules.errors.fetchModules.title")}
      info={t("addModules.errors.fetchModules.info")}
    />
  );
  const modalClasses = classNames("ActionPlanTimeline__modal", {
    "ActionPlanTimeline__modal--surveySettings":
      selectedActivity?.activityType === ActivityType.Survey,
  });

  return (
    <div className="ActionPlanTimeline">
      <div className="ActionPlanTimeline__actions">
        <Button
          variant="contained"
          color="secondary"
          small
          onClick={() => setAddActivityModalOpen(true)}
        >
          {"+ " + t("timeline.actions.add")}
        </Button>
      </div>
      <Box className="ActionPlanTimeline__container">
        {[
          lanes.inclusionPulse,
          lanes.microLearning,
          lanes.leadershipLearning,
        ].map((lane) => (
          <ActionPlanSwimlane
            key={lane.title}
            title={lane.title}
            icon={lane.icon}
            activities={lane.activities}
            startDate={startDate}
            endDate={endDate}
            now={now}
            preview={
              !hasSurveyAccess && lane.title === lanes.inclusionPulse.title
            }
            onActivityClick={handleActivityClick}
          />
        ))}

        <ActionPlanRail labels={labels} onNavigate={handleNavigate} />

        <ActionPlanNowIndicator
          now={now}
          startDate={startDate}
          endDate={endDate}
          label={t("timeline.now")}
        />
      </Box>

      <Modal
        className={modalClasses}
        open={activityDetailModalOpen}
        onClose={() => {
          setActivityDetailModalOpen(false);
          setDateChangeError(undefined);
        }}
        light
      >
        {selectedActivity && (
          <ActionPlanActivity
            activity={selectedActivity}
            onDateChange={handleChangeDate}
            onSave={() => onActivityUpdate(selectedActivity)}
            onDelete={() => onActivityRemove(selectedActivity)}
            surveyDispatch={getSurveyDispatch(selectedActivity ?? undefined)}
            onSurveyUpdate={onSurveyUpdate}
            isSettingUp={isSettingUp}
            onClose={() => setActivityDetailModalOpen(false)}
            dateChangeError={dateChangeError}
          />
        )}
      </Modal>

      <Modal
        open={addActivityModalOpen}
        onClose={() => setAddActivityModalOpen(false)}
        className="ActionPlanTimeline__addModuleModal"
      >
        {trainingModulesError ? (
          renderError()
        ) : (
          <ActionPlanTrainingWizard
            trainingModuleOptions={trainingModuleStates}
            onClose={() => setAddActivityModalOpen(false)}
            onActivitiesRefetch={onActivitiesRefetch}
            onActivitiesAdd={handleActivitiesAdd}
          />
        )}
      </Modal>
    </div>
  );
};
